import { type ProductsSettings } from "@fitness-app/data-models/entities/ProductsSettings";
import { getError } from "@fitness-app/utils";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { getTeamOwnerId } from "../../../helpers/getTeamOwnerId";
import { type AppThunk } from "../../../index";
import {
  subscribeToIntegrationFailed,
  subscribeToIntegrationStarted,
  subscribeToIntegrationSuccess,
  updateProductsSettings,
} from "../reducer";
import { fetchProductsSettings } from "./fetchProductsSettings";
import { unsubscribeFromProductsSettings } from "./unsubscribeFromProductsSettings";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToProductsSettings =
  (): AppThunk =>
  async (dispatch, getState, { auth, subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "productsSettings");

    try {
      dispatch(subscribeToIntegrationStarted());

      void dispatch(fetchProductsSettings());

      const currentUser = await getLoggedUser(auth);
      const id = getTeamOwnerId(currentUser);

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "productsSettings", claimedSlot)) {
        return;
      }

      const channelName = `products-settings-listener`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeProductsSettingsListChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "productsSettings", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.productsSettingsListChannelName = channelName;
      subscriptions.realtimeProductsSettingsListChannel = channel;

      subscriptions.realtimeProductsSettingsListChannel
        .on<ProductsSettings>(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "products_settings", filter: `userId=eq.${id}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateProductsSettings(payload.new));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToIntegrationSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromProductsSettings());
          }
        });
    } catch (error) {
      dispatch(subscribeToIntegrationFailed(getError(error)));
    }
  };
